import { capture } from "@/lib/posthog";
import React, { useEffect, useRef, useState } from "react";
import { AgentMark } from "@/components/AgentMark";
import {
  MCP_SERVER_URL,
  mcpClients,
  copyText,
  type McpClient,
  type McpClientId,
} from "@/lib/mcp";
import { connectMcpTestIds as ids } from "@/testids/connectMcpTestIds";
import "./connect-mcp-modal.css";
import { Icon } from "@/pages/prototype-icons";

/**
 * Picks an AI client and hands over whatever that client needs to reach fastn.
 *
 * Rendered in two places — the Connect dialog and Settings > MCP server — which
 * is why it is a panel rather than part of the modal. A settings page is where
 * a developer looks for an endpoint (it sits beside API keys), and a dialog is
 * not linkable from a support ticket; duplicating the markup to serve both is
 * how the two drift.
 *
 * Three shapes of client, and the grid is ordered by how little work each is:
 *   1. A one-click install link (Claude, Cursor, VS Code) — the tool opens with
 *      the server prefilled and the user only confirms. None of them will add a
 *      server from a link outright; that confirmation is the security boundary.
 *   2. A CLI command or a config file (Claude Code, Codex, Antigravity,
 *      Cline, Claude Desktop).
 *   3. A click-path through the tool's own UI (Devin, ChatGPT, Lovable, Bolt), which
 *      is why the server URL sits under the grid rather than inside each entry.
 *
 * The snippets themselves live in lib/mcp.ts, with the reason each client's
 * shape differs. They are not interchangeable.
 */

interface CopyFieldProps {
  value: string;
  label: string;
  multiline?: boolean;
  fieldTestId: string;
  buttonTestId: string;
  errorTestId: string;
}

/**
 * Each field owns its own copy state.
 *
 * A single shared "copy failed" flag put the warning wherever it was rendered
 * rather than beside the field that failed, told the user to copy the wrong
 * value, and let a successful copy of one field silently clear another's
 * warning. With every client on screen that is no longer a corner case.
 */
function CopyField({
  value,
  label,
  multiline,
  fieldTestId,
  buttonTestId,
  errorTestId,
}: CopyFieldProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A leftover timer from an earlier copy would revert the label moments after
  // the next one, reading as a failed copy; on unmount it would set state on a
  // dead component.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleCopy = async () => {
    const ok = await copyText(value);
    setFailed(!ok);
    if (!ok) return;
    // Only a SUCCESSFUL copy counts as taking the value away: a blocked
    // clipboard is a friction event, not a distribution one, and counting it
    // here would make the install funnel look healthier than it is.
    capture("mcp_url_copied", { target: label, method: "copy" });
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className={multiline ? "clUrl clUrl--multi" : "clUrl"}>
        <code data-testid={fieldTestId}>{value}</code>
        <button
          type="button"
          className="clCopy"
          aria-label={`Copy ${label}`}
          data-testid={buttonTestId}
          onClick={handleCopy}
        >
          {copied ? <Icon name="check" /> : <Icon name="copy" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {failed && (
        <p className="clWarn" data-testid={errorTestId}>
          Your browser blocked clipboard access. Select the {label.toLowerCase()} above and copy it
          manually.
        </p>
      )}
    </>
  );
}

/**
 * The selected client's setup, under the grid.
 *
 * One panel rather than a disclosure per client: with every client expandable in
 * place, the list pushed the server URL — the one value all of them need — far
 * below the fold, and nothing showed which client you were actually reading.
 */
function ClientDetail({
  client,
  panelId,
}: {
  client: McpClient;
  panelId?: string;
}): React.ReactElement {
  return (
    <div className="clDetail" id={panelId} data-testid={ids.clientPanel(client.id)}>
      <div className="clDetailHead">
        <AgentMark mark={client.mark} />
        <span className="clDetailName">{client.name}</span>
        <span className="clDetailBlurb">{client.blurb}</span>
      </div>

      {client.install && (
        <a
          className="clAdd"
          href={client.install.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={ids.clientInstallButton(client.id)}
          onClick={() => capture("mcp_install_link_opened", { client: client.id })}
        >
          <AgentMark mark={client.mark} />
          <span>{client.install.label}</span>
          <Icon name="link-external" />
        </a>
      )}

      {client.steps && (
        <ol className="clSteps" data-testid={ids.clientSteps(client.id)}>
          {client.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      {client.note && (
        <p className="clNote" data-testid={ids.clientNote(client.id)}>
          {client.note}
        </p>
      )}

      {client.snippets.map((snippet, i) => (
        <div className="clRow" key={snippet.label}>
          {snippet.title && <span className="clRowT">{snippet.title}</span>}
          {snippet.path && <span className="clPath">{snippet.path}</span>}
          <CopyField
            value={snippet.value}
            label={snippet.label}
            multiline={snippet.multiline}
            fieldTestId={ids.clientSnippetInput(client.id, i)}
            buttonTestId={ids.clientSnippetCopyButton(client.id, i)}
            errorTestId={ids.clientSnippetCopyErrorMessage(client.id, i)}
          />
        </div>
      ))}

      {client.altLinks?.map((link, i) => (
        <a
          key={link.href}
          className="clCopy clAltLink"
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={ids.clientAltLink(client.id, i)}
          onClick={() => capture("mcp_install_link_opened", { client: client.id })}
        >
          {link.label}
          <Icon name="link-external" />
        </a>
      ))}
    </div>
  );
}

/** The one expandable region the tiles below control. */
const DETAIL_PANEL_ID = "connect-mcp-client-detail";

export function ConnectMcpPanel(): React.ReactElement {
  const serverUrl = MCP_SERVER_URL;
  const clients = mcpClients(serverUrl);
  const featured = clients.find((c) => c.recommended);
  const rest = clients.filter((c) => !c.recommended);
  // Null until a tile is picked. The recommended client is already expanded, so
  // pre-selecting one of the others would put two setups on screen and leave it
  // ambiguous which one the URL at the bottom belongs to.
  const [selectedId, setSelectedId] = useState<McpClientId | null>(null);
  const selected = rest.find((c) => c.id === selectedId) ?? null;

  return (
    <>
      {featured && (
        <>
          <div className="clGroupT">Recommended</div>
          <ClientDetail client={featured} />
        </>
      )}

      {/* Everything else is a tile: their setups are a command or a config file
          and do not fit a card each, and a grid answers "is my tool supported?"
          at a glance, which a list of expanded panels did not. */}
      <div className="clGroupT">Other</div>
      {/* NOT a tablist. These were role="tab" in a role="tablist", which
          promises a matching tabpanel, an aria-controls pairing and arrow-key
          navigation — none of which this has, so a screen reader announced
          "tab 1 of 11" and then found no panel. They are buttons that expand a
          shared region below, which is exactly what aria-expanded and
          aria-controls describe. */}
      <div className="clGrid" role="group" aria-label="Other AI clients" data-testid={ids.grid}>
        {rest.map((client) => {
          const isSelected = client.id === selected?.id;
          return (
            <button
              key={client.id}
              type="button"
              aria-expanded={isSelected}
              aria-controls={isSelected ? DETAIL_PANEL_ID : undefined}
              className={isSelected ? "clTile clTile--on" : "clTile"}
              data-testid={ids.clientTile(client.id)}
              onClick={() => {
                // Clicking the open tile closes it, so the panel can be put
                // away without picking a different client.
                setSelectedId(isSelected ? null : client.id);
                // Which tools people actually reach for. Counted on select
                // rather than on copy because a client with an install link has
                // nothing to copy.
                if (!isSelected) capture("mcp_client_expanded", { client: client.id });
              }}
            >
              <AgentMark mark={client.mark} />
              <span className="clTileName">{client.name}</span>
            </button>
          );
        })}
      </div>

      {selected && <ClientDetail client={selected} panelId={DETAIL_PANEL_ID} />}

      {/* Last: the one-click card needs no URL at all, so this is here for the
          setups that do. Header and field on a single row, because the
          paragraph that used to explain it said what the two words either side
          of the field already say. */}
      <div className="clUrlRow">
        <span className="clUrlLabel">MCP server URL</span>
        <div className="clUrlField">
          <CopyField
            value={serverUrl}
            label="MCP server URL"
            fieldTestId={ids.serverUrlInput}
            buttonTestId={ids.serverUrlCopyButton}
            errorTestId={ids.serverUrlCopyErrorMessage}
          />
        </div>
      </div>
    </>
  );
}
